import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CloudArrowUpIcon, 
  DocumentIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  XMarkIcon,
  PlayIcon,
  PauseIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { encryptAudioBlob, generateEncryptionKey, exportKey, AUDIO_ENCRYPTION_CONFIG } from '@/utils/crypto';
import { recordingStorage, StoredRecording } from '@/services/recordingStorage';
import { useUploadJobStore } from '@/stores/uploadJobStore';
import { useAuth } from '@/hooks/useAuth';
import toast from 'react-hot-toast';

interface DebugAudioUploaderProps {
  appointmentId?: string;
  encounterId?: string;
  patientId: string;
  patientName?: string;
  encounterDate?: string;
  encounterTime?: string;
  onUploadComplete?: () => void;
}

interface UploadLog {
  id: string;
  timestamp: Date;
  message: string;
  type: 'info' | 'success' | 'error' | 'warning';
  details?: string;
}

interface FileValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

const DebugAudioUploader: React.FC<DebugAudioUploaderProps> = ({
  appointmentId,
  encounterId,
  patientId,
  patientName,
  encounterDate,
  encounterTime,
  onUploadComplete
}) => {
  const isEncounterMode = !!encounterId;
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [logs, setLogs] = useState<UploadLog[]>([]);
  const [currentStep, setCurrentStep] = useState<string>('');
  const [encryptedRecording, setEncryptedRecording] = useState<StoredRecording | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // File validation constants
  const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200 MB
  const ALLOWED_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/mp4', 'audio/m4a', 'audio/flac', 'audio/webm'];

  const addLog = (message: string, type: UploadLog['type'] = 'info', details?: string) => {
    const log: UploadLog = {
      id: `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
      timestamp: new Date(),
      message,
      type,
      details
    };
    setLogs(prev => [...prev, log]);
  };

  const validateFile = (file: File): FileValidation => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      errors.push(`File size (${formatFileSize(file.size)}) exceeds maximum allowed size (${formatFileSize(MAX_FILE_SIZE)})`);
    }

    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      errors.push(`File type "${file.type}" is not supported. Allowed types: ${ALLOWED_TYPES.join(', ')}`);
    }

    // Check file extension as fallback
    const extension = file.name.split('.').pop()?.toLowerCase();
    const allowedExtensions = ['mp3', 'wav', 'mp4', 'm4a', 'flac', 'webm'];
    if (!extension || !allowedExtensions.includes(extension)) {
      if (errors.length === 0) { // Only add if no type error already
        errors.push(`File extension ".${extension}" is not supported. Allowed extensions: ${allowedExtensions.join(', ')}`);
      }
    }

    // Warnings for large files
    if (file.size > 50 * 1024 * 1024) { // 50 MB
      warnings.push('Large file detected. Processing may take longer.');
    }

    // Warnings for non-MP3 files
    if (!file.type.includes('mp3') && !file.name.toLowerCase().endsWith('.mp3')) {
      warnings.push('Non-MP3 files may have reduced compatibility. MP3 is recommended.');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    addLog(`File selected: ${file.name}`, 'info', `Size: ${formatFileSize(file.size)}, Type: ${file.type}`);
    
    const validation = validateFile(file);
    
    if (!validation.isValid) {
      addLog('File validation failed', 'error');
      validation.errors.forEach(error => addLog(error, 'error'));
      setSelectedFile(null);
      return;
    }

    if (validation.warnings.length > 0) {
      validation.warnings.forEach(warning => addLog(warning, 'warning'));
    }

    setSelectedFile(file);
    addLog('File validation passed', 'success');
  };

  const handlePlayPause = () => {
    if (!audioUrl) return;

    const audio = document.getElementById('debugAudioPlayer') as HTMLAudioElement;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !user) return;

    setIsProcessing(true);
    setUploadProgress(0);
    setLogs([]);
    setCurrentStep('Initializing upload process...');

    try {
      // Step 1: Read file
      setCurrentStep('Reading audio file...');
      addLog('Reading audio file into memory', 'info');
      setUploadProgress(10);

      const arrayBuffer = await selectedFile.arrayBuffer();
      const audioBlob = new Blob([arrayBuffer], { type: selectedFile.type });
      addLog('File read successfully', 'success', `Size: ${formatFileSize(audioBlob.size)}`);

      // Step 2: Generate encryption key
      setCurrentStep('Generating encryption key...');
      addLog('Generating AES-256-GCM encryption key', 'info');
      setUploadProgress(20);

      const encryptionKey = await generateEncryptionKey();
      addLog('Encryption key generated', 'success');

      // Step 3: Encrypt audio
      setCurrentStep('Encrypting audio data...');
      addLog('Encrypting audio with AES-256-GCM', 'info');
      setUploadProgress(30);

      const encrypted = await encryptAudioBlob(audioBlob, encryptionKey);
      addLog('Audio encrypted successfully', 'success', `Algorithm: ${AUDIO_ENCRYPTION_CONFIG.algorithm}`);

      // Step 4: Create stored recording object
      setCurrentStep('Preparing recording metadata...');
      addLog('Creating recording metadata', 'info');
      setUploadProgress(40);

      const encryptionMetadata = {
        iv: encrypted.iv,
        authTag: encrypted.authTag,
        algorithm: AUDIO_ENCRYPTION_CONFIG.algorithm,
        keyId: await exportKey(encryptionKey)
      };

      const storedRecording: StoredRecording = {
        id: `debug-${Date.now()}`,
        ...(isEncounterMode ? { encounterId } : { appointmentId }),
        patientId,
        providerId: user.id,
        encryptedAudio: encrypted.encryptedData,
        encryptionMetadata,
        recordingMetadata: {
          duration: 0, // Will be calculated by backend
          size: audioBlob.size,
          mimeType: audioBlob.type,
          recordedAt: new Date(),
          deviceInfo: {
            userAgent: navigator.userAgent,
            platform: navigator.platform
          }
        },
        uploadStatus: 'pending',
        uploadAttempts: 0
      };

      addLog('Recording metadata created', 'success');

      // Step 5: Save to local storage (the upload job + crash-recovery/retry read it from here)
      setCurrentStep('Saving to local storage...');
      addLog('Saving encrypted recording to IndexedDB', 'info');
      setUploadProgress(85);

      await recordingStorage.saveRecording(storedRecording);
      setEncryptedRecording(storedRecording);
      addLog('Recording saved to local storage', 'success');

      // Step 6: Hand off to the background upload job. It owns createRecording + upload + the
      // transcription poll, plus the persistent progress toast, navigation survival, and retry —
      // the same path the live recorder uses. Not awaited: the toast takes over from here.
      setCurrentStep('Handed off to background upload');
      setUploadProgress(100);

      const previewUrl = URL.createObjectURL(audioBlob);
      setAudioUrl(previewUrl);

      void useUploadJobStore.getState().runUpload({
        encryptedRecording: storedRecording,
        recordingTime: storedRecording.recordingMetadata.duration || 0,
        isEncounterMode,
        appointmentId: isEncounterMode ? undefined : appointmentId,
        encounterId: isEncounterMode ? encounterId : undefined,
        patientName,
        encounterDate,
        encounterTime,
      });

      addLog(
        'Handed off to background upload — progress continues in the toast',
        'success',
        'Upload + transcription run in the background; the toast shows completion, retry, or errors.'
      );

      toast.success('Upload started — track progress in the toast.');

      if (onUploadComplete) {
        onUploadComplete();
      }

    } catch (error) {
      // Only the pre-hand-off steps (read / encrypt / save) can throw here; once handed off, the
      // upload job owns terminal status and surfaces any failure in the toast.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addLog('Upload failed before hand-off', 'error', errorMessage);
      setCurrentStep('Upload failed');
      toast.error(`Upload failed: ${errorMessage}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setEncryptedRecording(null);
    setAudioUrl(null);
    setIsPlaying(false);
    setLogs([]);
    setCurrentStep('');
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const clearLogs = () => {
    setLogs([]);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="backdrop-blur-md bg-white/70 rounded-2xl border border-white/20 shadow-lg p-6 space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
            <CloudArrowUpIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Debug Audio Uploader</h3>
            <p className="text-sm text-gray-600">Upload local audio files for testing</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEncounterMode && (
            <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded font-medium">
              CONNECTED
            </div>
          )}
          <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
            DEBUG MODE
          </div>
        </div>
      </div>

      {/* File Selection */}
      <div className="space-y-4">
        <input
          ref={fileInputRef}
          type="file"
          accept=".mp3,.wav,.mp4,.m4a,.flac,.webm,audio/*"
          onChange={handleFileSelect}
          disabled={isProcessing}
          hidden
          className="hidden"
        />
        <div className="flex items-center space-x-4">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isProcessing}
            className="btn-secondary flex items-center space-x-2"
          >
            <DocumentIcon className="w-4 h-4" />
            <span>Choose Audio File</span>
          </button>

          {selectedFile && (
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <CheckCircleIcon className="w-4 h-4 text-green-500" />
              <span>{selectedFile.name}</span>
              <span className="text-gray-400">({formatFileSize(selectedFile.size)})</span>
            </div>
          )}
        </div>

        {/* File Info */}
        {selectedFile && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-4"
          >
            <h4 className="font-medium text-blue-900 mb-2">File Information</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-blue-800">Name:</span>
                <span className="ml-2 text-blue-700">{selectedFile.name}</span>
              </div>
              <div>
                <span className="font-medium text-blue-800">Size:</span>
                <span className="ml-2 text-blue-700">{formatFileSize(selectedFile.size)}</span>
              </div>
              <div>
                <span className="font-medium text-blue-800">Type:</span>
                <span className="ml-2 text-blue-700">{selectedFile.type}</span>
              </div>
              <div>
                <span className="font-medium text-blue-800">Last Modified:</span>
                <span className="ml-2 text-blue-700">
                  {new Date(selectedFile.lastModified).toLocaleString()}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Upload Progress */}
      {isProcessing && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">{currentStep}</span>
            <span className="text-sm text-gray-600">{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div 
              className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${uploadProgress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </motion.div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center space-x-3">
        <button
          onClick={handleUpload}
          disabled={!selectedFile || isProcessing}
          className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600 disabled:hover:shadow-md disabled:hover:translate-y-0"
        >
          <CloudArrowUpIcon className="w-4 h-4" />
          <span>Upload & Transcribe</span>
        </button>

        {audioUrl && (
          <button
            onClick={handlePlayPause}
            className="btn-secondary flex items-center space-x-2"
          >
            {isPlaying ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
            <span>{isPlaying ? 'Pause' : 'Preview'}</span>
          </button>
        )}

        <button
          onClick={handleReset}
          disabled={!selectedFile || isProcessing}
          className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-600"
        >
          <TrashIcon className="w-4 h-4" />
          <span>Reset</span>
        </button>
      </div>

      {/* Audio Player (hidden) */}
      {audioUrl && (
        <audio 
          id="debugAudioPlayer" 
          src={audioUrl} 
          className="hidden"
          onEnded={() => setIsPlaying(false)}
        />
      )}

      {/* Logs Panel */}
      <AnimatePresence>
        {logs.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-gray-50 border border-gray-200 rounded-lg p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-800">Upload Logs</h4>
              <button
                onClick={clearLogs}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {logs.map((log) => (
                <motion.div 
                  key={log.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`flex items-start space-x-3 p-2 rounded text-sm ${
                    log.type === 'error' ? 'bg-red-50 text-red-800' :
                    log.type === 'success' ? 'bg-green-50 text-green-800' :
                    log.type === 'warning' ? 'bg-yellow-50 text-yellow-800' :
                    'bg-blue-50 text-blue-800'
                  }`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {log.type === 'error' && <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />}
                    {log.type === 'success' && <CheckCircleIcon className="w-4 h-4 text-green-500" />}
                    {log.type === 'warning' && <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" />}
                    {log.type === 'info' && <div className="w-4 h-4 bg-blue-500 rounded-full" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{log.message}</div>
                    {log.details && (
                      <div className="text-xs opacity-75 mt-1">{log.details}</div>
                    )}
                    <div className="text-xs opacity-50 mt-1">
                      {log.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Instructions */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-800 mb-2">Instructions</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Select an audio file (MP3, WAV, MP4, M4A, FLAC, WEBM)</li>
          <li>• Maximum file size: 200 MB</li>
          <li>• File will be encrypted with AES-256-GCM before upload</li>
          <li>• Same encryption and upload flow as live recording</li>
          <li>• Transcription will be triggered automatically</li>
        </ul>
      </div>
    </motion.div>
  );
};

export default DebugAudioUploader;
